export interface AnyQuestUser {
  uid: string
  email: string | null
  displayName: string | null
  isAnonymous: boolean
  getIdToken(): Promise<string>
}

// ============================================================
// TYPES.TS — THE LAW
// Central TypeScript interfaces for AnyQuest.
// Every AI response must be validated against these shapes.
// ============================================================

type ValueEnumMap<T extends readonly string[]> = {
  readonly [K in T[number]]: K
}

function defineValueEnum<const T extends readonly string[]>(keys: T): ValueEnumMap<T> {
  const out: Record<string, string> = {}
  for (const key of keys) out[key] = key
  return out as ValueEnumMap<T>
}

export const CombatStatus = defineValueEnum(['active', 'fled', 'victory', 'defeat'] as const)
export type CombatStatus = typeof CombatStatus[keyof typeof CombatStatus]

export const ActionCategory = defineValueEnum([
  'examine',
  'take',
  'use',
  'attack',
  'move',
  'interact',
  'ability',
  'other',
] as const)
export type ActionCategory = typeof ActionCategory[keyof typeof ActionCategory]

export const ActionSource = defineValueEnum([
  'player',
  'companion',
  'npc',
  'item',
  'event',
  'system',
] as const)
export type ActionSource = typeof ActionSource[keyof typeof ActionSource]

export const ActionMode = defineValueEnum(['test', 'do'] as const)
export type ActionMode = typeof ActionMode[keyof typeof ActionMode]

export const MotivePriority = defineValueEnum(['high', 'medium', 'low'] as const)
export type MotivePriority = typeof MotivePriority[keyof typeof MotivePriority]

export const Sentiment = defineValueEnum(['positive', 'negative', 'neutral'] as const)
export type Sentiment = typeof Sentiment[keyof typeof Sentiment]

export const GameOrigin = defineValueEnum([
  'local',
  'imported_blueprint',
  'imported_scenario',
  'imported_vessel',
  'imported_starter',
] as const)
export type GameOrigin = typeof GameOrigin[keyof typeof GameOrigin]

// --- Attribute & Modifier ---

export interface Attribute {
  val: number;
  desc: string;
  fullName?: string;
  aiGuidance?: string;
}

export interface Modifier {
  val: number;
  label: string;
  source: string;
  expiresAt?: number;
}

export interface Skill {
  id: string;
  name: string;
  level: number;
  desc?: string;
}

export interface Ability {
  id: string;
  name: string;
  desc: string;
  cooldown: number;
  lastUsed?: number;
  logicFn?: string;
  args?: {
    name: string
    type: 'target' | 'number' | 'choice' | 'text'
    prompt: string
    options?: string[];
    min?: number;
    max?: number;
    step?: number;
    defaultValue?: string | number;
    ui?: 'combo' | 'slider' | 'input'
  }[]
}

export interface Trait {
  name: string
  desc: string
}

export interface Vitals {
  vitality: number;
  spirit: number;
  essence: number;
  maxVitality: number
  maxSpirit: number
  maxEssence: number
}

export type AttributeKey = 'STR' | 'DEX' | 'CON' | 'INT' | 'WIS' | 'CHA' | 'PER' | 'LCK' | 'WIL' | 'FOR' | 'AGI' | 'PRS'

export interface InventoryItem {
  name: string
  equipped: boolean
  desc?: string
  icon?: string
  worth?: number
  actions?: ItemActionDescriptor[]
}

export interface ForgedItemPayload {
  name: string
  desc?: string
  icon?: string
  worth?: number
  actions?: ItemActionDescriptor[]
}

export type ItemActionAvailability = 'inventory' | 'room' | 'any'
export type ItemActionTargetHint = 'none' | 'self' | 'active_npc'
export type ItemActionExecution = 'shared' | 'item_forge'

export interface ItemActionDescriptor {
  id: string
  label: string
  inputTemplate: string
  mode: ActionMode
  category: ActionCategory
  when?: ItemActionAvailability
  targetHint?: ItemActionTargetHint
  execution?: ItemActionExecution
  tags?: string[]
}

export interface DormantEffect {
  label: string
  source: string
  expiredAt: number
  attr?: AttributeKey
}

export interface DormantCondition {
  key: string
  label: string
  clearedAt: number
}

export interface LivingEntityStats {
  vitals: Vitals
  attributes: Record<AttributeKey, Attribute>
  modifiers: Partial<Record<AttributeKey, Modifier[]>>
  dormantEffects?: DormantEffect[];
  skills: Skill[]
  abilities: Ability[]
  traits: Trait[]
  inventory: InventoryItem[]
}

export interface NPCFeeling {
  sentiment: Sentiment
  intensity: number;
}

export type NpcStance = 'Hostile' | 'Wary' | 'Open'
export interface NPC {
  name: string
  identity: string;
  lore: string;
  sex?: string;
  stance?: NpcStance;
  intentions?: string[];
  feelings?: Record<string, NPCFeeling>;
  relationship?: number;
  memory?: string[];
  inventory?: string[];
  activeEffects?: string[];
  dormantEffects?: DormantEffect[];
}

export interface CompanionMotive {
  desc: string
  priority: MotivePriority
}

export interface CompanionFeeling {
  target: string;
  sentiment: Sentiment
  intensity: number;
}

export interface ConversationEntry {
  from: string;
  to: string;
  text: string
  gameClock: string;
}

export interface Companion extends LivingEntityStats {
  name: string
  identity: string;
  bio: string;
  sex?: string;
  portraitUrl?: string
  motives: CompanionMotive[]
  feelings: CompanionFeeling[]
  conversations: ConversationEntry[]
  location?: { x: number; y: number; z?: number };
  separated?: boolean;
  companions?: Companion[]
  }

  export interface Vessel extends LivingEntityStats {
  name?: string;
  identity: string;
  bio: string
  sex?: string
  location: { x: number; y: number; z?: number };
  }

export interface SpawningRule {
  archetype: string
  level: number
  chancePerHour: number
  lootInfo: string
  friendliness: number
  alertness: string
}

export interface AtlasNode {
  name: string
  desc?: string;
  items?: string[];
  flags?: Record<string, string | number | boolean | null>;
  dormantFlags?: DormantCondition[];
  clocks?: ProgressClock[];
  lastVisited?: number;
  visionUrl?: string;
  spawns?: SpawningRule[];
  lastSpawnCheck?: number;
}

export interface AtlasEdge {
  x1: number
  y1: number
  z1?: number
  x2: number
  y2: number
  z2?: number
  risk?: number // Travel danger: 0 (safe) to 1 (lethal)
}

export interface Atlas {
  nodes: Record<string, AtlasNode>;
  edges: AtlasEdge[]
}

export interface Clock {
  day: number
  hour: number
  minute: number
}

export interface ProgressClock {
  id: string
  label: string
  segments: number
  filled: number
  trigger?: string
}

export interface Room {
  name: string
  desc: string
  items: string[]
  npcs: NPC[]
  flags?: Record<string, string | boolean | number | null>
  dormantFlags?: DormantCondition[]
  clocks?: ProgressClock[]
}

export type QuestStatus = 'active' | 'completed' | 'failed' | 'dormant' | 'potential'
export type QuestType = 'quest' | 'mini-quest' | 'wish' | 'want'
export const QUEST_VALID_TRANSITIONS: Partial<Record<QuestStatus, QuestStatus[]>> = {
  potential: ['active'],
  active:    ['completed', 'failed'],
  dormant:   ['active'],
}
export interface Quest {
  id: string
  title: string
  desc: string
  type: QuestType
  status: QuestStatus
  dependsOn?: string[];
  created: number;
  completed?: number;
}

export interface HistoryEntry {
  location: string
  time: string;
  text: string;
  suggestions: Suggestion[]
  context: {
    items: string[]
    npcs: NPC[]
  }
}

export interface Suggestion {
  short: string;
  full: string;
}

export interface ModalAction {
  label: string
  danger?: boolean
  input: string;
  topic?: string;
  topicData?: unknown;
  source?: ActionSource
  mode?: ActionMode
  category?: ActionCategory
  explicitTargets?: ActionTargetRef[]
  tool?: ActionToolRef
  tags?: string[]
}

export interface CompanionSheetData {
  identity: string
  bio?: string
  vitals: Vitals
  abilities: Array<{ name: string; desc?: string; cooldown?: number }>
  traits: Array<{ name: string; desc?: string }>
  inventory: string[]
  activeEffects: string[]
  dormantEffects: string[]
  overflow?: {
    abilities?: number
    traits?: number
    inventory?: number
    activeEffects?: number
    dormantEffects?: number
  }
}

export interface NpcSheetData {
  identity: string
  lore: string
  sex?: string
  relationship: number
  relationshipLabel: string
  relationshipTone: 'friendly' | 'neutral' | 'hostile'
  intentions: string[]
  memories: string[]
  inventory: string[]
  activeEffects: string[]
  dormantEffects: string[]
  overflow?: {
    intentions?: number
    memories?: number
    inventory?: number
    activeEffects?: number
    dormantEffects?: number
  }
}

export interface NotebookSection {
  key: string
  label: string
  entries: string[]
  overflow?: number
}

export type FocusModalVariant = 'default' | 'notebook' | 'worldlog' | 'profile' | 'companion'
export interface GameDialogShellOptions {
  title?: string
  desc?: string
  meta?: string
  actions?: ModalAction[]
  variant?: FocusModalVariant
  blockClose?: boolean
  blockCloseMessage?: string
  swipeDismiss?: boolean
  showFooter?: boolean
  showCloseButton?: boolean
  closeLabel?: string
}

export interface GameDialogOptions {
  content: string
  data?: Record<string, unknown>
  options?: GameDialogShellOptions
}

export interface CombatLogEntry {
  round: number
  actor: 'player' | 'npc' | 'companion'
  actorName?: string
  action: string;
  outcome: string;
}

export interface CombatFoeState {
  name: string
  vitality: number;
}

export interface CombatState {
  npc: NPC
  round: number
  initiative: ('player' | string)[];
  npcVitality: number;
  playerAtStart: number;
  status: CombatStatus
  log: CombatLogEntry[]
  foes?: CombatFoeState[];
  allies?: string[];
  activeTargetName?: string;
  turnQueue?: ('player' | string)[];
  turnCursor?: number;
}

export interface ActionOption {
  id: string;
  label: string
  type: 'attack' | 'ability' | 'item'
}

export interface WorldNPC {
  id: string;
  name: string
  identity: string
  lore: string
  sex?: string
  intentions: string[]
  feelings: Record<string, NPCFeeling>
  location: string;
  lastActed?: number;
  relationship?: number;
  memory?: string[];
  inventory?: string[]
  activeEffects?: string[]
  dormantEffects?: DormantEffect[]
}

export interface WorldEvent {
  time: string;
  text: string;
  type: 'npc' | 'quest' | 'world' | 'environment'
}

export interface WorldTickUpdate {
  clockUpdates?: Record<string, number>
  npcUpdates?: { 
    id: string
    moveTo?: string
    intentions?: string[]
    feelings?: Record<string, NPCFeeling>
    relationship?: number;
    memoryAdd?: string;
    activeEffects?: string[]
    dormantEffectsAdd?: {
      label: string
      source?: string
      attr?: AttributeKey
    }
  }[]
  newNpcs?: {
    location: string
    name: string
    identity: string
    lore: string
    sex?: string
    intentions: string[]
  }[]
  newItems?: {
    location: string
    name: string
  }[]
  questUpdates?: { id: string; newStatus: QuestStatus; note?: string }[]
  newNodes?: { key: string; name: string; desc: string }[]
  worldEvents?: string[]
  roomFlagUpdates?: { roomKey: string; flags: Record<string, string | number | boolean | null> }[]
}

export interface WorldEnrichment {
  narrative: string
  newQuests?: Omit<Quest, 'created' | 'completed'>[]
  npcEnrichments?: { 
    id: string
    intentions?: string[]
    lore?: string
    relationship?: number
    memoryAdd?: string
  }[]
}

export type WorldPresentation = {
  variantName: string
  style: Record<string, string>
}

/**
 * Encapsulates a complete color scheme for a game world.
 * Generated by AI at Genesis or transition, ensuring WCAG compliance.
 */
export class WorldColorScheme {
  // Base Surface Tokens
  '--bg': string = '#06060a';
  '--surface': string = '#14141e';
  '--surface-raised': string = '#1a1a28';
  '--surface-card-1': string = '#12121e';
  '--surface-card-2': string = '#181828';
  '--surface-card-3': string = '#0b0b14';
  '--border': string = '#2a2a3c';
  '--border-subtle': string = '#1a1a28';

  // Text & Content
  '--text-prose': string = '#f0ece4';
  '--text-muted': string = '#c8bfb2';
  '--text-dim': string = '#9a8f80';
  '--text-sys': string = '#f5b731';

  // Brand & Accents
  '--brand-primary': string = '#818cf8';
  '--brand-primary-glow': string = 'rgba(129, 140, 248, 0.15)';
  '--brand-secondary': string = '#fbbf24';
  '--brand-secondary-glow': string = 'rgba(251, 191, 36, 0.15)';
  '--gold': string = '#f5b731';
  '--gold-glow': string = 'rgba(245, 183, 49, 0.15)';

  // Semantic Feedback (WCAG AA compliant)
  '--sem-success-fg': string = '#34d399';
  '--sem-success-bg': string = 'rgba(6, 78, 59, 0.16)';
  '--sem-success-border': string = 'rgba(52, 211, 153, 0.52)';
  '--sem-warn-fg': string = '#fde68a';
  '--sem-warn-bg': string = 'rgba(146, 64, 14, 0.16)';
  '--sem-warn-border': string = 'rgba(251, 191, 36, 0.52)';
  '--sem-danger-fg': string = '#fecaca';
  '--sem-danger-bg': string = 'rgba(127, 29, 29, 0.48)';
  '--sem-danger-border': string = 'rgba(248, 113, 113, 0.52)';
  '--sem-info-fg': string = '#c7d2fe';
  '--sem-info-bg': string = 'rgba(67, 56, 202, 0.14)';
  '--sem-info-border': string = 'rgba(129, 140, 248, 0.52)';

  // Entity Chips
  '--chip-ally-fg': string = '#c4b5fd';
  '--chip-ally-border': string = '#818cf8';
  '--chip-ally-surface-a': string = '#28204a';
  '--chip-ally-surface-b': string = '#1c1636';
  '--chip-npc-fg': string = '#fde68a';
  '--chip-npc-border': string = '#f5b731';
  '--chip-npc-surface-a': string = '#362a16';
  '--chip-npc-surface-b': string = '#241c0e';
  '--chip-item-fg': string = '#93c5fd';
  '--chip-item-border': string = '#3b82f6';
  '--chip-item-surface-a': string = '#1a2a3e';
  '--chip-item-surface-b': string = '#111c2d';
  '--chip-flag-fg': string = '#94a3b8';
  '--chip-flag-border': string = '#475569';
  '--chip-flag-surface-a': string = '#1e293b';
  '--chip-flag-surface-b': string = '#0f172a';

  // Vitals & Mechanics
  '--vital-hp': string = '#e8453a';
  '--vital-sp': string = '#818cf8';
  '--vital-es': string = '#f5b731';
  '--dice-glow': string = 'rgba(251, 191, 36, 0.48)';

  // Complex Gradients
  '--grad-main-bg': string = 'linear-gradient(180deg, var(--surface) 0%, var(--bg) 100%)';
  '--grad-overlay-fade': string = 'linear-gradient(180deg, transparent 0%, var(--bg) 100%)';
  '--grad-surface-shine': string = 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, transparent 100%)';

  constructor(data: Partial<WorldColorScheme> = {}) {
    const { luminanceCache: _ignoredCache, ...rest } = (data ?? {}) as Partial<WorldColorScheme> & {
      luminanceCache?: unknown
    }
    Object.assign(this, rest)
    this.luminanceCache = new Map<string, number>()
    this.enforceAccessibility()
  }

  private luminanceCache = new Map<string, number>();

  private hexToRgb(hex: string): { r: number, g: number, b: number } {
    const h = hex.replace('#', '')
    if (h.length === 3) return this.hexToRgb('#' + h[0] + h[0] + h[1] + h[1] + h[2] + h[2])
    const r = parseInt(h.substring(0, 2), 16)
    const g = parseInt(h.substring(2, 4), 16)
    const b = parseInt(h.substring(4, 6), 16)
    return { r, g, b }
  }

  /** Calculates relative luminance per WCAG 2.1. */
  private getLuminance(hex: string): number {
    if (this.luminanceCache.has(hex)) return this.luminanceCache.get(hex)!
    const { r, g, b } = this.hexToRgb(hex)
    const [rs, gs, bs] = [r, g, b].map(v => {
      const s = v / 255
      return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
    })
    const lum = 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
    this.luminanceCache.set(hex, lum)
    return lum
  }

  private getContrastRatio(foreground: string, background: string): number {
    const l1 = this.getLuminance(foreground) + 0.05
    const l2 = this.getLuminance(background) + 0.05
    return l1 > l2 ? l1 / l2 : l2 / l1
  }

  /**
   * Bumps the lightness of a color until it hits the target contrast ratio.
   */
  private ensureContrast(fgKey: keyof WorldColorScheme, bgKey: keyof WorldColorScheme, target = 4.5): void {
    let fg = String(this[fgKey] || '')
    const bg = String(this[bgKey] || '')
    if (!fg.startsWith('#') || !bg.startsWith('#')) return

    let currentRatio = this.getContrastRatio(fg, bg)
    if (currentRatio >= target) return

    // Implementation of a Safe Adjustment loop
    const rgb = this.hexToRgb(fg)
    let { h, s, l } = this.rgbToHsl(rgb.r, rgb.g, rgb.b)
    const bgLum = this.getLuminance(bg)
    const isDarkBg = bgLum < 0.5

    let attempts = 0
    while (currentRatio < target && attempts < 20) {
      if (isDarkBg) l = Math.min(100, l + 5)
      else l = Math.max(0, l - 5)
      
      const newRgb = this.hslToRgb(h, s, l)
      fg = this.rgbToHex(newRgb.r, newRgb.g, newRgb.b)
      currentRatio = this.getContrastRatio(fg, bg)
      attempts++
    }

    (this as any)[fgKey] = fg
  }

  private rgbToHsl(r: number, g: number, b: number) {
    r /= 255
    g /= 255
    b /= 255
    const max = Math.max(r, g, b), min = Math.min(r, g, b)
    let h = 0, s, l = (max + min) / 2
    if (max === min) { h = s = 0 }
    else {
      const d = max - min
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break
        case g: h = (b - r) / d + 2; break
        case b: h = (r - g) / d + 4; break
      }
      h /= 6
    }
    return { h: h * 360, s: s * 100, l: l * 100 }
  }

  private hslToRgb(h: number, s: number, l: number) {
    h /= 360
    s /= 100
    l /= 100
    let r, g, b
    if (s === 0) { r = g = b = l }
    else {
      const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1
        if (t > 1) t -= 1
        if (t < 1/6) return p + (q - p) * 6 * t
        if (t < 1/2) return q
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6
        return p
      }
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s
      const p = 2 * l - q
      r = hue2rgb(p, q, h + 1/3)
      g = hue2rgb(p, q, h)
      b = hue2rgb(p, q, h - 1/3)
    }
    return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) }
  }

  private rgbToHex(r: number, g: number, b: number): string {
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)
  }

  enforceAccessibility(): void {
    this.ensureContrast('--text-prose', '--bg', 4.5)
    this.ensureContrast('--gold', '--bg', 3.0)
  }

  /** Returns all tokens as a flat record for CSS injection. */
  toCSSVariables(): Record<string, string> {
    const vars: Record<string, string> = {}
    for (const [key, value] of Object.entries(this)) {
      if (key.startsWith('--')) vars[key] = value as string
    }
    return vars
  }

  toJSON(): Record<string, string> {
    return this.toCSSVariables()
  }

  static fromAI(json: any): WorldColorScheme {
    if (!json || typeof json !== 'object') return new WorldColorScheme()
    return new WorldColorScheme(json)
  }
}

export interface WorldEconomy {
  currencyName: string
  currencySymbol: string
  rationCost: number // Basis value: cost of 1 day of food
}

export interface WorldTaxonomy {
  worldName: string
  coreReality: string
  itemCategories: { pattern: string; category: string }[]
  visualNpcArchetypes: { pattern: string; key: string; label: string }[]
  combatNpcArchetypes: { pattern: string; archetype: string; style: string }[]
  imageStyle: string
  colorScheme: WorldColorScheme
  economy: WorldEconomy
}

export interface GameEventChoice {
  label: string;
  input: string;
}

export interface GameEventState {
  id: string;
  title: string;
  desc: string;
  imageKind?: 'item' | 'npc' | 'scene' | 'companion';
  imageSeed?: string;
  choices: GameEventChoice[];
  resolved?: boolean;
}

export interface GameState {
  meta: {
    name: string
    origin: GameOrigin
    timestamp: number
  }
  player: Vessel
  companions: Companion[];
  journal: Quest[];
  notebook?: string[];
  world: {
    seed: string
    clock: Clock
    clocks?: ProgressClock[]
    scheduledJobs?: ScheduledEffectJob[]
    narrative?: string;
    npcs: WorldNPC[];
    lastTick?: number;
    lastEnrichment?: number;
    taxonomy?: WorldTaxonomy
  }
  atlas: Atlas
  currentRoom: Room
  history: HistoryEntry[]
  worldLog: WorldEvent[];
  combat?: CombatState | null;
  activeEvent?: GameEventState | null;
  visionUrl?: string
  portraitUrl?: string
  mainQuestCompleted?: boolean;
  gameEnded?: boolean;
}

export interface LogicArtifactReplacements {
  activeEvent?: GameEventState | null;
}

export interface LogicUpdate {
  successLevel: number;
  updates: {
    vitality?: number;
    spirit?: number;
    essence?: number;
    timeCost: number;
    mods?: Partial<Record<AttributeKey, Modifier>>
    inventoryAdd?: Array<string | ForgedItemPayload>
    inventoryRemove?: string[]
    inventoryEquip?: string[];
    inventoryUnequip?: string[];
    roomItemsAdd?: Array<string | ForgedItemPayload>
    roomItemsRemove?: string[]
    roomNpcsAdd?: Array<{ name: string; identity: string; lore?: string; sex?: string }>;
    roomFlags?: Record<string, string | boolean | number | null>;
    clockUpdates?: Record<string, number>;
    questUpdates?: { id: string; newStatus: QuestStatus }[];
    npcRelationshipUpdates?: Record<string, number>;
    npcMemoryAdd?: Record<string, string>;
    scheduleJobs?: ScheduledEffectJobRequest[]
    artifactReplacements?: LogicArtifactReplacements
    /** @deprecated Use artifactReplacements.activeEvent instead. */
    triggerEvent?: GameEventState;
    /** @deprecated Use artifactReplacements.activeEvent = null instead. */
    clearEvent?: boolean;
  }
}

export type ScheduledEffectJobKind = 'trigger_event' | 'world_log'

export interface ScheduledEffectJobRequest {
  kind: ScheduledEffectJobKind
  delayMinutes: number
  note?: string
  event?: GameEventState
}

export interface ScheduledEffectJob {
  id: string
  kind: ScheduledEffectJobKind
  dueMinute: number
  note?: string
  event?: GameEventState
}

export interface RawGenesisCompanion {
  name: string
  identity: string
  bio: string
  sex?: string
  vitals: Vitals
  attributes: Record<AttributeKey, number>;
  skills: Skill[]
  abilities: Ability[]
  traits: Trait[]
  inventory: string[]
  motives: CompanionMotive[]
  feelings: CompanionFeeling[]
  companions?: RawGenesisCompanion[]
}

export interface GenesisAIResponse {
  name?: string;
  identity?: string;
  sex?: string
  stats: Record<AttributeKey, number>
  traits: Trait[]
  skills: Skill[]
  abilities: Ability[]
  inventory?: string[]
  quests?: Array<Pick<Quest, 'id' | 'title' | 'desc' | 'type' | 'status'>>
  companions: RawGenesisCompanion[]
  room: {
    name: string
    desc: string;
    narration: string;
    items: string[]
    npcs: NPC[]
    suggestions: Suggestion[]
  }
}

export interface LogicForgeMeta {
  cacheable: boolean;
  category: ActionCategory
}

export interface ActionActorRef {
  kind: 'player' | 'companion' | 'npc' | 'system'
  id: string
  label: string
}

export interface ActionTargetRef {
  kind: 'player' | 'companion' | 'npc' | 'item' | 'room' | 'world' | 'event'
  id: string
  label: string
}

export interface ActionToolRef {
  kind: 'ability' | 'item' | 'attack' | 'event-choice' | 'free-text'
  id: string
  label: string
}

export interface ActionEnvelope {
  source: ActionSource
  mode: ActionMode
  input: string
  category?: ActionCategory
  actor: ActionActorRef
  targets: ActionTargetRef[]
  tool?: ActionToolRef
  args?: Record<string, string | number | boolean>
  tags?: string[]
}

export interface ActionExecutionContext {
  action: ActionEnvelope
  actorLabel: string
  targetLabels: string[]
  roomName: string
  roomDesc?: string
  worldTime: string
  inCombat: boolean
  roll: number
  mechanics?: {
    dc: number
    primaryStat: string
    bonus: number
    chance: number
  }
}

export interface LogicForgeAIResponse {
  logicFn: string;
  meta: LogicForgeMeta
  mechanics?: {
    dc: number;
    primaryStat: string;
    bonus: number;
    chance: number;
  }
}

export interface NarratorAIResponse {
  prose: string
  suggestions: Suggestion[]
  newRoom?: {
    name: string
    desc: string
    xOff: number
    yOff: number
    zOff?: number
    items: string[]
    npcs: NPC[]
  }
  atlasHints?: {
    places?: Array<{
      key: string
      name: string
      desc?: string
    }>
    paths?: Array<{
      from: string
      to: string
      risk?: number
    }>
  }
}

export interface TraitShiftAIResponse {
  traits: Trait[]
}

export interface NpcModEffect {
  val: number
  label: string
  source: string
  duration?: number;
}

export interface NpcLogicUpdate {
  hit: boolean
  vitalityDelta: number;
  spiritDelta?: number;
  essenceDelta?: number;
  mods?: Partial<Record<AttributeKey, NpcModEffect>>;
  inventorySteal?: string;
  outcome: string;
}

export interface NpcCacheEntry {
  fn: string
  archetype: string
  createdAt: number
}

export interface WorkerRequest {
  logicFnString: string
  stateSnapshot: GameState;
  roll: number
  npcMode?: boolean;
  executionContext?: ActionExecutionContext
  mechanics?: {
    dc: number
    primaryStat: string
    bonus: number
    chance: number
  }
}

export interface WorkerResponse {
  success: boolean
  result: LogicUpdate | NpcLogicUpdate
}

// --- Import & Gallery Types ---

export interface ImportedWorldData {
  seed: string
  atlas: Atlas
  room: Room
}

export interface ImportedPlayerData {
  name?: string
  identity: string
  bio: string
  sex?: string
  vitals: Vitals
  attributes: Record<AttributeKey, Attribute>
  skills: Skill[]
  abilities: Ability[]
  traits: Trait[]
  inventory: InventoryItem[]
}

export interface WorldGalleryEntry {
  saveId: string
  seed: string
  roomName: string
  saveName: string
  playerIdentity: string
  origin: GameOrigin
  timestamp: number
}

export interface VesselGalleryEntry {
  key: string
  identity: string
  bio: string
  sex?: string
  portraitUrl?: string
  saveName: string
  roomName: string
  timestamp: number
  origin: GameOrigin
  abilityCount: number
  skillCount: number
  traitCount: number
  playerData: Vessel
}
